import { useCallback, useEffect, useState } from 'react';
import { DOCUMENT_VERSIONS, DocumentType } from '@/lib/constants/documentVersions';
import { useAuth } from '@/contexts/AuthContext';
import { useConsentTracking } from '@/hooks/useConsentTracking';

const STORAGE_KEY = 'acknowledgedVersions';

type AcknowledgedMap = Partial<Record<DocumentType, string>>;

const readMap = (): AcknowledgedMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

const writeMap = (map: AcknowledgedMap) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
};

/**
 * Generic acknowledgement hook for legal-document version updates.
 *
 * Tracks per-document acknowledgement state in `localStorage.acknowledgedVersions`
 * (a JSON map of `{ documentType: versionString }`) and, for authenticated users,
 * writes a corresponding `<documentType>_acknowledged` row to the `user_consents`
 * audit trail via the `log-consent` edge function.
 *
 * Treats users with no localStorage entry AND no prior acknowledgement as "new"
 * (no banner shown) — they are assumed to have implicitly accepted current
 * versions through the signup flow (`AuthForm`).
 *
 * IMPORTANT — informational acknowledgement ≠ consent renewal:
 * Bumping `DOCUMENT_VERSIONS.cookiePolicy` only triggers the informational
 * `PolicyUpdateBanner`; it does NOT clear the `cookie-consent` localStorage
 * key that drives the actual TDDDG § 25 consent banner. If the *cookies or
 * storage mechanisms themselves* change (new tracker, new category, scope
 * expansion), additionally clear `localStorage.removeItem('cookie-consent')`
 * during the migration to force re-consent. See
 * mem://compliance/cookie-policy-standard for the rule.
 */
export const useDocumentAcknowledgement = (documentType: DocumentType) => {
  const { user } = useAuth();
  const { logConsent } = useConsentTracking();

  const currentVersion = DOCUMENT_VERSIONS[documentType];
  const [lastAckedVersion, setLastAckedVersion] = useState<string | null>(() => {
    return readMap()[documentType] ?? null;
  });

  // Sync if storage was changed in another tab
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setLastAckedVersion(readMap()[documentType] ?? null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [documentType]);

  // needsAck only fires if a *prior* version has been acknowledged and is now stale.
  // First-time visitors (lastAckedVersion === null) see no banner.
  const needsAck = lastAckedVersion !== null && lastAckedVersion !== currentVersion;

  const acknowledge = useCallback(async () => {
    const map = readMap();
    map[documentType] = currentVersion;
    writeMap(map);
    setLastAckedVersion(currentVersion);

    if (user?.id) {
      try {
        // Normalize camelCase documentType to snake_case for clean audit queries
        // (mem://compliance/user-consents-audit-trail).
        // e.g. "privacyPolicy" -> "privacy_policy_acknowledged"
        const snakeType = documentType
          .replace(/([A-Z])/g, '_$1')
          .toLowerCase();
        await logConsent(
          {
            consentType: `${snakeType}_acknowledged`,
            documentVersion: currentVersion,
          },
          user.id,
        );
      } catch (err) {
        console.error('Failed to log acknowledgement consent:', err);
      }
    }
  }, [documentType, currentVersion, user?.id, logConsent]);

  /**
   * Mark current versions as the implicit baseline (no consent record written).
   * Used by AuthForm after signup so a fresh user does not see the banner on
   * their next visit, since they accepted the current versions implicitly.
   */
  const markCurrentAsBaseline = useCallback(() => {
    const map = readMap();
    map[documentType] = currentVersion;
    writeMap(map);
    setLastAckedVersion(currentVersion);
  }, [documentType, currentVersion]);

  return {
    needsAck,
    currentVersion,
    lastAckedVersion,
    acknowledge,
    markCurrentAsBaseline,
  };
};
